/**
 * Bike Build Page
 * ================
 * Restoration/build progress for a motorcycle with photo galleries,
 * work checklist, and cost breakdown.
 */

import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useMotorcycle } from '@/hooks/useMotorcycles'
import BikePageHeader from './BikePageHeader'
import { motorcycleApi } from '@/services/api'
import type { MotorcycleImage } from '@/types'
import {
  Hammer,
  Camera,
  ChevronDown,
  ChevronRight,
  Check,
  DollarSign,
  Truck,
  Wrench,
  Package,
  X,
  ChevronLeft,
  Loader2,
} from 'lucide-react'

// =============================================================================
// Types
// =============================================================================

interface PhotoGallery {
  id: string
  title: string
  description: string
  count: number
  coverImage: string
  images: { url: string; caption?: string }[]
}

interface WorkCategory {
  id: string
  title: string
  items: { task: string; completed: boolean; details?: string }[]
}

interface CostCategory {
  category: string
  amount: number
  items?: { name: string; amount: number }[]
}

// =============================================================================
// Static Data for XT350 Restoration
// =============================================================================

// Gallery metadata (photo URLs are loaded from API)
const GALLERY_METADATA: Record<string, { title: string; description: string }> = {
  before: {
    title: 'Before',
    description: 'Condition when purchased in June 2022',
  },
  during: {
    title: 'During',
    description: 'Restoration work in progress',
  },
  after: {
    title: 'After',
    description: 'Completed restoration - September 2022',
  },
}

const XT350_BUILD_DATA = {
  // Restoration work checklist
  workCategories: [
    {
      id: 'frame',
      title: 'Frame & Chassis',
      items: [
        { task: 'Frame sandblasted', completed: true },
        { task: 'Frame powder coated', completed: true, details: 'RAL 3020 Traffic Red' },
        { task: 'All frame bolts replaced with new', completed: true },
      ],
    },
    {
      id: 'front-suspension',
      title: 'Front Suspension',
      items: [
        { task: 'Front forks rebuilt', completed: true },
        { task: 'New fork seals installed', completed: true, details: 'Mikes XS SKU: AB56-120' },
        { task: 'Fork tubes polished', completed: true },
        { task: 'Fork bottoms sandblasted and painted', completed: true },
        { task: 'Install new copper washer on left fork drain bolt', completed: false, details: 'Left fork leaking at drain bolt' },
      ],
    },
    {
      id: 'rear-suspension',
      title: 'Rear Suspension',
      items: [
        { task: 'New rear shock absorber', completed: true, details: 'YSS MZ456-370TRL-04' },
        { task: 'Rear shock pivot seal replaced', completed: true, details: 'Mikes XS SKU: 93109-18020-00' },
        { task: 'Swingarm glass blasted and painted', completed: true },
        { task: 'All new swingarm bearings and bushings', completed: true },
        { task: 'All new linkage bearings and bushings', completed: true },
      ],
    },
    {
      id: 'front-wheel',
      title: 'Front Wheel',
      items: [
        { task: 'Front rim polished', completed: true },
        { task: 'New stainless steel spokes', completed: true, details: 'Polished SS + nickel brass nipples (CWC)' },
        { task: 'New front tire', completed: true },
        { task: 'New tube', completed: true },
        { task: 'New front wheel bearings', completed: true, details: 'Mikes XS SKU: AB25-1444' },
      ],
    },
    {
      id: 'rear-wheel',
      title: 'Rear Wheel',
      items: [
        { task: 'New old stock (NOS) rear rim', completed: true },
        { task: 'New stainless steel spokes', completed: true, details: 'Polished SS + nickel brass nipples (CWC)' },
        { task: 'New rear tire', completed: true },
        { task: 'New tube', completed: true },
        { task: 'New rear wheel bearings', completed: true, details: 'Mikes XS SKU: AB25-1231' },
      ],
    },
    {
      id: 'brakes',
      title: 'Brakes',
      items: [
        { task: 'Front brake caliper rebuilt and painted', completed: true },
        { task: 'Rear brake rebuilt and painted', completed: true },
        { task: 'New front brake master cylinder', completed: true },
        { task: 'New brake pads front', completed: true },
        { task: 'New brake shoes rear', completed: true },
        { task: 'Brake rod spring, pin, adjuster nut replaced', completed: true },
        { task: 'Banjo bolt replaced', completed: true, details: '10mm x 1.25mm' },
      ],
    },
    {
      id: 'drivetrain',
      title: 'Drivetrain',
      items: [
        { task: 'New chain', completed: true },
        { task: 'Rear sprocket replaced', completed: true, details: '55T, 428 pitch, steel, black' },
        { task: 'Front sprocket replaced', completed: true, details: '19T, 428 pitch, steel, silver' },
        { task: 'New plastic chain guard', completed: true },
        { task: 'New chain rollers and guides', completed: true },
        { task: 'Clutch plate set replaced', completed: true, details: 'EBC fiber plates' },
        { task: 'Clutch springs replaced', completed: true, details: 'Pack of 6' },
      ],
    },
    {
      id: 'cables',
      title: 'Cables & Controls',
      items: [
        { task: 'Throttle cable - push and pull', completed: true },
        { task: 'Tachometer cable', completed: true },
        { task: 'Speedometer cable', completed: true },
        { task: 'Clutch cable', completed: true },
        { task: 'Clutch lever dust cover', completed: true },
        { task: 'New handlebars', completed: true, details: 'Same style, not original' },
        { task: 'New levers both sides', completed: true },
        { task: 'New grips', completed: true },
      ],
    },
    {
      id: 'fuel',
      title: 'Fuel System',
      items: [
        { task: 'Petcock repair kit', completed: true },
        { task: 'Oil filter replaced', completed: true, details: 'Cartridge type' },
        { task: 'New crankcase breather', completed: true, details: 'Right side' },
      ],
    },
    {
      id: 'carb',
      title: 'Carburetor',
      items: [
        { task: 'Carburetors ultrasonic cleaned', completed: true },
        { task: 'All new needles', completed: true },
        { task: 'All new springs', completed: true },
        { task: 'All new gaskets', completed: true },
        { task: 'New intake manifold / carb boot', completed: true },
      ],
    },
    {
      id: 'electrical',
      title: 'Electrical',
      items: [
        { task: 'Wiring redone with color correct wires', completed: true },
        { task: 'Connectors replaced', completed: true },
        { task: 'All circuits tested', completed: true },
        { task: 'New ignition coil', completed: true },
        { task: 'New spark plugs', completed: true },
        { task: 'New battery', completed: true },
        { task: 'New horn', completed: true },
        { task: 'New battery (replacement)', completed: false, details: 'Previous battery dead/depleted' },
      ],
    },
    {
      id: 'engine',
      title: 'Engine',
      items: [
        { task: 'New bolts in most of motor', completed: true },
        { task: 'New bolts in engine covers (both sides)', completed: true },
      ],
    },
    {
      id: 'exhaust',
      title: 'Exhaust',
      items: [
        { task: 'New front exhaust pipe', completed: true, details: 'Predator Exhausts, stainless steel' },
        { task: 'New muffler', completed: true, details: 'DG Performance V2 Slip-On' },
        { task: 'New exhaust gaskets', completed: true },
        { task: 'New exhaust bolts', completed: true },
      ],
    },
    {
      id: 'bodywork',
      title: 'Bodywork & Cosmetics',
      items: [
        { task: 'New OEM front fender', completed: true },
        { task: 'Tank professionally painted', completed: true, details: 'SB málun' },
        { task: 'Rear fender professionally painted', completed: true, details: 'SB málun' },
        { task: 'New rear fender sub-plastics', completed: true },
        { task: 'New number plate holder', completed: true },
        { task: 'New seat cover', completed: true },
        { task: 'New OEM mirrors (both sides)', completed: true },
        { task: 'New graphics / decals', completed: true },
        { task: 'Apply new graphics on front number plate', completed: false },
        { task: 'Apply new XT tank stickers', completed: false, details: 'Replacing blue lettering with black' },
      ],
    },
    {
      id: 'final-assembly',
      title: 'Final Assembly',
      items: [
        { task: 'Go over all nuts and bolts', completed: false, details: 'Final torque check / inspection' },
      ],
    },
  ] as WorkCategory[],

  // Cost breakdown
  costs: {
    summary: {
      purchasePrice: 250000,
      parts: 744587,
      shipping: 196557,
      labour: 190000,
      tools: 38976,
      tracked: 1420120,
      estimated: 1750000,
    },
    categories: [
      { category: 'Purchase Price', amount: 250000 },
      {
        category: 'Parts',
        amount: 744587,
        items: [
          { name: 'Mikes XS (cables, seals, clutch)', amount: 40432 },
          { name: 'Central Wheel Components (spokes)', amount: 23374 },
          { name: 'CMS Orders (OEM parts)', amount: 282120 },
          { name: 'eBay Purchases (various)', amount: 353661 },
          { name: 'Biking Viking (tires)', amount: 45000 },
        ],
      },
      {
        category: 'Shipping',
        amount: 196557,
        items: [
          { name: 'Mikes XS shipping', amount: 13789 },
          { name: 'CWC shipping', amount: 12794 },
          { name: 'Brook Suspension shipping', amount: 9270 },
          { name: 'eBay import charges & shipping', amount: 160704 },
        ],
      },
      {
        category: 'Labour',
        amount: 190000,
        items: [
          { name: 'Sandblasting (frame, forks, etc.)', amount: 40000 },
          { name: 'Painting (tank, rear fender)', amount: 150000 },
        ],
      },
      { category: 'Tools & Supplies', amount: 38976 },
    ] as CostCategory[],
  },

  // Project timeline
  timeline: {
    started: 'June 2022',
    completed: 'September 2022',
    status: '99% Complete',
    totalHours: '~300 hours',
  },

  // Paint specs
  paintSpecs: {
    frame: { color: 'Traffic Red', code: 'RAL 3020', finish: 'Powder coat' },
    tank: { color: 'Fire Red', code: 'Yamaha 00FH', finish: 'Paint' },
    fenders: { color: 'Fire Red', code: 'Yamaha 00FH', finish: 'Paint' },
  },
}

// =============================================================================
// Components
// =============================================================================

/**
 * Photo Gallery Card - Shows preview and opens lightbox on click
 */
function GalleryCard({
  gallery,
  onClick,
}: {
  gallery: PhotoGallery
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="group relative bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 text-left w-full"
    >
      {/* Cover image */}
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={gallery.coverImage}
          alt={gallery.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder-image.png'
          }}
        />
      </div>

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

      {/* Content overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
        <h3 className="text-xl font-bold">{gallery.title}</h3>
        <p className="text-white/80 text-sm mt-1">{gallery.description}</p>
        <div className="flex items-center gap-2 mt-2 text-white/70 text-sm">
          <Camera size={14} />
          <span>{gallery.count} photos</span>
        </div>
      </div>

      {/* Hover indicator */}
      <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Camera size={20} className="text-white" />
      </div>
    </button>
  )
}

/**
 * Lightbox component for viewing gallery images
 */
function Lightbox({
  gallery,
  initialIndex,
  onClose,
}: {
  gallery: PhotoGallery
  initialIndex: number
  onClose: () => void
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const currentImage = gallery.images[currentIndex]

  const goNext = () => {
    setCurrentIndex((i) => (i + 1) % gallery.images.length)
  }

  const goPrev = () => {
    setCurrentIndex((i) => (i - 1 + gallery.images.length) % gallery.images.length)
  }

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') goNext()
    else if (e.key === 'ArrowLeft') goPrev()
    else if (e.key === 'Escape') onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors z-10"
      >
        <X size={32} />
      </button>

      {/* Gallery title */}
      <div className="absolute top-4 left-4 text-white z-10">
        <h3 className="text-xl font-bold">{gallery.title}</h3>
        <p className="text-white/70 text-sm">
          {currentIndex + 1} of {gallery.images.length}
        </p>
      </div>

      {/* Previous button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          goPrev()
        }}
        className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
      >
        <ChevronLeft size={32} />
      </button>

      {/* Image */}
      <div
        className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={currentImage.url}
          alt={currentImage.caption || `Photo ${currentIndex + 1}`}
          className="max-w-full max-h-[75vh] object-contain"
        />
        {currentImage.caption && (
          <p className="text-white/80 text-center mt-4 px-4">{currentImage.caption}</p>
        )}
      </div>

      {/* Next button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          goNext()
        }}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
      >
        <ChevronRight size={32} />
      </button>

      {/* Thumbnail strip */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto py-2 px-4">
        {gallery.images.map((img, idx) => (
          <button
            key={idx}
            onClick={(e) => {
              e.stopPropagation()
              setCurrentIndex(idx)
            }}
            className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-all ${
              idx === currentIndex
                ? 'border-white scale-110'
                : 'border-transparent opacity-50 hover:opacity-75'
            }`}
          >
            <img src={img.url} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * Expandable work category section
 */
function WorkSection({ category }: { category: WorkCategory }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const completedCount = category.items.filter((i) => i.completed).length

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown size={20} className="text-gray-400" />
          ) : (
            <ChevronRight size={20} className="text-gray-400" />
          )}
          <span className="font-medium text-gray-900 dark:text-white">{category.title}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {completedCount}/{category.items.length}
          </span>
          {completedCount === category.items.length && (
            <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
              <Check size={12} className="text-white" />
            </div>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="p-4 bg-white dark:bg-gray-800 space-y-2">
          {category.items.map((item, idx) => (
            <div key={idx} className="flex items-start gap-3 py-1">
              <div
                className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center mt-0.5 ${
                  item.completed
                    ? 'bg-green-500 text-white'
                    : 'border-2 border-gray-300 dark:border-gray-600'
                }`}
              >
                {item.completed && <Check size={12} />}
              </div>
              <div>
                <span className="text-gray-900 dark:text-white">{item.task}</span>
                {item.details && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{item.details}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Cost breakdown table
 */
function CostBreakdown({ costs }: { costs: typeof XT350_BUILD_DATA.costs }) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)

  const formatISK = (amount: number) => {
    return new Intl.NumberFormat('is-IS').format(amount) + ' ISK'
  }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-1">
            <DollarSign size={16} />
            <span className="text-sm font-medium">Tracked</span>
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white">
            {formatISK(costs.summary.tracked)}
          </div>
        </div>
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
            <Package size={16} />
            <span className="text-sm font-medium">Parts</span>
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white">
            {formatISK(costs.summary.parts)}
          </div>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 mb-1">
            <Truck size={16} />
            <span className="text-sm font-medium">Shipping</span>
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white">
            {formatISK(costs.summary.shipping)}
          </div>
        </div>
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-1">
            <Wrench size={16} />
            <span className="text-sm font-medium">Labour</span>
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white">
            {formatISK(costs.summary.labour)}
          </div>
        </div>
      </div>

      {/* Detailed breakdown */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Category
              </th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Amount (ISK)
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {costs.categories.map((cat) => (
              <>
                <tr
                  key={cat.category}
                  className={`${
                    cat.items ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30' : ''
                  }`}
                  onClick={() =>
                    cat.items &&
                    setExpandedCategory(expandedCategory === cat.category ? null : cat.category)
                  }
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {cat.items && (
                        <span className="text-gray-400">
                          {expandedCategory === cat.category ? (
                            <ChevronDown size={16} />
                          ) : (
                            <ChevronRight size={16} />
                          )}
                        </span>
                      )}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {cat.category}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right font-medium text-gray-900 dark:text-white">
                    {formatISK(cat.amount)}
                  </td>
                </tr>
                {cat.items && expandedCategory === cat.category && (
                  <>
                    {cat.items.map((item, idx) => (
                      <tr key={idx} className="bg-gray-50/50 dark:bg-gray-700/20">
                        <td className="py-2 px-4 pl-12 text-sm text-gray-600 dark:text-gray-400">
                          {item.name}
                        </td>
                        <td className="py-2 px-4 text-right text-sm text-gray-600 dark:text-gray-400">
                          {formatISK(item.amount)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-100 dark:bg-gray-700 font-bold">
              <td className="py-3 px-4 text-gray-900 dark:text-white">Estimated Total</td>
              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">
                {formatISK(costs.summary.estimated)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
        Note: Living in Iceland means shipping adds significant cost to parts orders. Some costs
        (powder coating, wheel building, misc tools) are estimated.
      </p>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function BikeBuild() {
  const { bikeId } = useParams<{ bikeId: string }>()
  const { data: bike, isLoading, error } = useMotorcycle(bikeId || '')
  const [lightboxGallery, setLightboxGallery] = useState<PhotoGallery | null>(null)
  const [galleries, setGalleries] = useState<PhotoGallery[]>([])
  const [photosLoading, setPhotosLoading] = useState(false)

  // Fetch build photos from API
  useEffect(() => {
    const fetchPhotos = async () => {
      if (!bikeId) return

      setPhotosLoading(true)
      try {
        const response = await motorcycleApi.getBuildPhotos(bikeId)
        if (response.data) {
          const photoData = response.data

          // Convert API response to gallery format
          const newGalleries: PhotoGallery[] = []

          const categoryOrder = ['before', 'during', 'after'] as const
          for (const category of categoryOrder) {
            const photos = photoData[category] || []
            if (photos.length > 0) {
              const metadata = GALLERY_METADATA[category] || {
                title: category.charAt(0).toUpperCase() + category.slice(1),
                description: `${photos.length} photos`,
              }

              newGalleries.push({
                id: category,
                title: metadata.title,
                description: metadata.description,
                count: photos.length,
                coverImage: photos[0].image_url,
                images: photos.map((img: MotorcycleImage) => ({
                  url: img.image_url,
                  caption: img.caption || undefined,
                })),
              })
            }
          }

          setGalleries(newGalleries)
        }
      } catch (err) {
        console.error('Failed to fetch build photos:', err)
      } finally {
        setPhotosLoading(false)
      }
    }

    fetchPhotos()
  }, [bikeId])

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load motorcycle</p>
      </div>
    )
  }

  // Check if this is the XT350 (has build data)
  const isXT350 = bike?.motorcycle_id === 'MOTO-0001'
  const buildData = isXT350 ? XT350_BUILD_DATA : null
  const hasPhotos = galleries.length > 0

  return (
    <div>
      <BikePageHeader bike={bike} isLoading={isLoading} />

      {isLoading ? (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          </div>
        </div>
      ) : buildData || hasPhotos ? (
        <div className="space-y-8">
          {/* Project Overview - only for XT350 */}
          {buildData && (
            <div className="bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl p-6 text-white">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold mb-2">Restoration Project</h1>
                  <p className="text-amber-100">
                    Complete frame-off restoration of a 1986 Yamaha XT 350
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                    <div className="text-amber-200 text-xs">Started</div>
                    <div className="font-semibold">{buildData.timeline.started}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                    <div className="text-amber-200 text-xs">Completed</div>
                    <div className="font-semibold">{buildData.timeline.completed}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                    <div className="text-amber-200 text-xs">Status</div>
                    <div className="font-semibold">{buildData.timeline.status}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2">
                    <div className="text-amber-200 text-xs">Hours</div>
                    <div className="font-semibold">{buildData.timeline.totalHours}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Photo Galleries */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Camera size={20} className="text-primary-600 dark:text-primary-400" />
              Photo Documentation
            </h2>
            {photosLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                <span className="ml-2 text-gray-500 dark:text-gray-400">Loading photos...</span>
              </div>
            ) : galleries.length > 0 ? (
              <div className="grid md:grid-cols-3 gap-6">
                {galleries.map((gallery) => (
                  <GalleryCard
                    key={gallery.id}
                    gallery={gallery}
                    onClick={() => setLightboxGallery(gallery)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Camera size={32} className="mx-auto mb-2 opacity-50" />
                <p>No build photos have been uploaded yet.</p>
              </div>
            )}
          </div>

          {/* Work Completed - XT350 only */}
          {buildData && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Hammer size={20} className="text-primary-600 dark:text-primary-400" />
                Work Completed
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {buildData.workCategories.map((category) => (
                  <WorkSection key={category.id} category={category} />
                ))}
              </div>
            </div>
          )}

          {/* Cost Breakdown - XT350 only */}
          {buildData && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <DollarSign size={20} className="text-primary-600 dark:text-primary-400" />
                Project Costs
              </h2>
              <CostBreakdown costs={buildData.costs} />
            </div>
          )}

          {/* Paint Specifications - XT350 only */}
          {buildData && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Paint Specifications
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="font-medium text-gray-900 dark:text-white mb-2">Frame</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p>{buildData.paintSpecs.frame.color}</p>
                    <p className="font-mono">{buildData.paintSpecs.frame.code}</p>
                    <p className="text-xs mt-1">{buildData.paintSpecs.frame.finish}</p>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="font-medium text-gray-900 dark:text-white mb-2">Tank</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p>{buildData.paintSpecs.tank.color}</p>
                    <p className="font-mono">{buildData.paintSpecs.tank.code}</p>
                    <p className="text-xs mt-1">{buildData.paintSpecs.tank.finish}</p>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="font-medium text-gray-900 dark:text-white mb-2">Fenders</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p>{buildData.paintSpecs.fenders.color}</p>
                    <p className="font-mono">{buildData.paintSpecs.fenders.code}</p>
                    <p className="text-xs mt-1">{buildData.paintSpecs.fenders.finish}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Placeholder for other bikes */
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
          <div className="text-center py-12">
            <Hammer size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Restoration / Build
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              Restoration and build progress for {bike?.name || 'this motorcycle'} has not yet been
              documented.
            </p>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxGallery && (
        <Lightbox
          gallery={lightboxGallery}
          initialIndex={0}
          onClose={() => setLightboxGallery(null)}
        />
      )}
    </div>
  )
}
